import type { Recipe } from '@/types/recipe'

interface AIStatusResponse {
  configured: boolean
}

const STATUS_CACHE_KEY = 'ai-recipe-image-status'
const STATUS_CACHE_TTL_MS = 10 * 60 * 1000

export interface AIRecipeImageResult {
  success: boolean
  imageUrl?: string
  error?: string
}

type AIImageRequestBody = {
  title?: string
  description?: string
  ingredients?: string[]
  tags?: string[]
  mealType?: string
}

class AIRecipeImageService {
  private statusCache: boolean | null = null

  private async request<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
    const response = await fetch(input, {
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
      ...init,
    })

    const data = (await response.json()) as T & { error?: string }

    if (!response.ok) {
      throw new Error(data.error || 'AI afbeeldingsverzoek mislukt')
    }

    return data
  }

  private readPersistedStatus(): boolean | null {
    if (typeof window === 'undefined') {
      return null
    }

    try {
      const raw = window.sessionStorage.getItem(STATUS_CACHE_KEY)
      if (!raw) {
        return null
      }

      const parsed = JSON.parse(raw) as { configured: boolean; timestamp: number }
      if (Date.now() - parsed.timestamp > STATUS_CACHE_TTL_MS) {
        window.sessionStorage.removeItem(STATUS_CACHE_KEY)
        return null
      }

      return parsed.configured
    } catch {
      return null
    }
  }

  private persistStatus(configured: boolean) {
    if (typeof window === 'undefined') {
      return
    }

    window.sessionStorage.setItem(
      STATUS_CACHE_KEY,
      JSON.stringify({ configured, timestamp: Date.now() })
    )
  }

  peekStatus(): boolean | null {
    if (this.statusCache !== null) {
      return this.statusCache
    }

    const persistedStatus = this.readPersistedStatus()
    if (persistedStatus !== null) {
      this.statusCache = persistedStatus
    }

    return this.statusCache
  }

  async getStatus(forceRefresh = false): Promise<boolean> {
    if (!forceRefresh) {
      const cachedStatus = this.peekStatus()
      if (cachedStatus !== null) {
        return cachedStatus
      }
    }

    try {
      const data = await this.request<AIStatusResponse>('/api/ai/recipe-image', {
        method: 'GET',
      })
      this.statusCache = data.configured
      this.persistStatus(data.configured)
      return data.configured
    } catch {
      this.statusCache = false
      this.persistStatus(false)
      return false
    }
  }

  async generateRecipeImage(recipe: Partial<Recipe>): Promise<AIRecipeImageResult> {
    const configured = await this.getStatus()
    if (!configured) {
      return {
        success: false,
        error: 'AI is nog niet platformbreed geconfigureerd. Voeg OPENAI_API_KEY toe in Vercel.',
      }
    }

    const body: AIImageRequestBody = {
      title: recipe.title,
      description: recipe.description,
      ingredients: recipe.ingredients,
      tags: recipe.tags,
      mealType: recipe.meal_type,
    }

    try {
      return await this.request<AIRecipeImageResult>('/api/ai/recipe-image', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Onbekende fout bij AI afbeeldingsgeneratie',
      }
    }
  }
}

export const aiRecipeImage = new AIRecipeImageService()
